import { create } from 'zustand'
import { api } from '../lib/api'
import { onSocketEvent } from '../lib/socket'

/**
 * Maintenance-state store. One source of truth for:
 *   - the public marketing site (visited before sign-in)
 *   - the app shells (which redirect their users to /maintenance when
 *     their panel is in maintenance)
 *   - the /maintenance screen itself (which redirects users back out
 *     when the toggle is flipped off)
 *
 * Two refresh paths:
 *   1. `/maintenance/status` polled on bootstrap (public endpoint, no
 *       auth required) and on the screen every 10 s as a fallback
 *       when sockets aren't connected (e.g. unauthenticated visitor).
 *   2. Socket event `maintenance.changed` — pushed the moment Super
 *       Admin presses Save, so signed-in tabs flip in real time.
 *
 * The store also tracks a "current scope" — the scope key the active
 * user is gated by. Resolution mirrors the backend's
 * `resolveMaintenanceScope` so we don't have a divergent definition.
 */

const SCOPES = ['website', 'company', 'user']

function defaultState() {
  return Object.fromEntries(
    SCOPES.map((s) => [
      s,
      { enabled: false, message: '', scheduledStart: null, scheduledEnd: null },
    ]),
  )
}

/** Normalise an incoming scope payload from the server. Pulled out so
 *  `refresh()` and `applyServerState()` use the same shape and the
 *  `scheduledStart` / `scheduledEnd` fields don't get silently dropped
 *  on either path. */
function normalizeScope(v) {
  return {
    enabled: Boolean(v?.enabled),
    message: typeof v?.message === 'string' ? v.message : '',
    scheduledStart:
      typeof v?.scheduledStart === 'string' ? v.scheduledStart : null,
    scheduledEnd:
      typeof v?.scheduledEnd === 'string' ? v.scheduledEnd : null,
  }
}

/** Mirror of the backend's resolveMaintenanceScope. Returns null for
 *  exempt users (super_admin) and for unknown roles. All workRoles in
 *  the User shell (Manager, Team Leader, every other workRole) share
 *  the single `user` switch — one toggle gates the whole panel. */
export function scopeForUser(user) {
  if (!user) return 'website'
  if (user.role === 'super_admin') return null
  if (user.role === 'company_admin' || user.role === 'sub_admin') return 'company'
  if (user.role === 'user') return 'user'
  return null
}

let pollTimer = null
let socketUnsubscribe = null
let visibilityHandler = null

export const useMaintenanceStore = create((set, get) => ({
  state: defaultState(),
  // 'unknown' while we wait for the first fetch — the route guard
  // shows nothing instead of flashing the maintenance screen before
  // we know whether the toggle is on.
  status: 'unknown',

  /** Pull the latest state from the public status endpoint. Tolerant
   *  of network errors so a brief blip doesn't lock the user out. */
  async refresh() {
    try {
      // Cache-buster param so no browser/CDN cache can pin a stale state
      // (the backend also sends Cache-Control: no-store).
      const res = await api.get('/maintenance/status', {
        params: { t: Date.now() },
      })
      const data = res.data?.data
      if (data && typeof data === 'object') {
        const next = defaultState()
        for (const s of SCOPES) {
          if (data[s]) next[s] = normalizeScope(data[s])
        }
        set({ state: next, status: 'ready' })
      }
    } catch {
      // Treat unreachable status as "no maintenance" so the app keeps
      // working when the API is briefly down. The auth-pipeline 503
      // path still surfaces an issue if the user can't actually use
      // the panel.
      set({ status: 'ready' })
    }
  },

  /** Apply a fresh state pushed by the backend's `maintenance.changed`
   *  socket event. Same shape as `refresh()` but skips the HTTP fetch. */
  applyServerState(serverState) {
    if (!serverState || typeof serverState !== 'object') return
    const next = defaultState()
    for (const s of SCOPES) {
      if (serverState[s]) next[s] = normalizeScope(serverState[s])
    }
    set({ state: next, status: 'ready' })
  },

  /** Mark a scope down locally without waiting for the next refresh.
   *  Fired from the axios interceptor when an API call returns a 503
   *  with `code: 'maintenance'` so the affected tab flips into the
   *  maintenance screen on the very next render. */
  markScopeDown(scope, message) {
    if (!SCOPES.includes(scope)) return
    set((prev) => ({
      state: {
        ...prev.state,
        [scope]: { enabled: true, message: message ?? prev.state[scope].message },
      },
      status: 'ready',
    }))
  },

  /** Convenience selector — is the panel `scope` currently down?
   *  Null scope (super_admin) is always allowed. */
  isScopeDown(scope) {
    if (!scope) return false
    return Boolean(get().state[scope]?.enabled)
  },

  /** Start the bootstrap + listener. Idempotent so React 18 strict-
   *  mode double-invocations don't queue two pollers. */
  start() {
    if (pollTimer || socketUnsubscribe) return
    void get().refresh()
    // Background poll is the FALLBACK. Signed-in tabs get instant updates
    // over the socket; signed-OUT tabs (sign-in / maintenance page) have no
    // socket, so a shorter interval plus the focus / visibility re-check
    // below keep them snappy without a socket per anonymous visitor.
    pollTimer = setInterval(() => {
      void get().refresh()
    }, 10_000)
    socketUnsubscribe = onSocketEvent('maintenance.changed', (payload) => {
      get().applyServerState(payload)
    })
    // Re-check the instant the tab regains focus / becomes visible.
    visibilityHandler = () => {
      if (
        typeof document === 'undefined' ||
        document.visibilityState !== 'hidden'
      ) {
        void get().refresh()
      }
    }
    window.addEventListener('focus', visibilityHandler)
    document.addEventListener('visibilitychange', visibilityHandler)
  },

  stop() {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
    if (socketUnsubscribe) {
      socketUnsubscribe()
      socketUnsubscribe = null
    }
    if (visibilityHandler) {
      window.removeEventListener('focus', visibilityHandler)
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
  },
}))

// Bridge from the axios interceptor (which can't import this store
// directly without an import cycle through lib/api.js → store →
// lib/api.js) to the markScopeDown action. The interceptor dispatches
// `taskmagix:maintenance-hit` on every 503 with code: 'maintenance'.
if (typeof window !== 'undefined') {
  window.addEventListener('taskmagix:maintenance-hit', (event) => {
    const { scope, message } = event.detail ?? {}
    useMaintenanceStore.getState().markScopeDown(scope, message)
  })
}
